<template>
  <div class="relative size-full">
    <div
      class="transition-opacity delay-1000 duration-1000 ease-out"
      :class="[
        ready ? 'opacity-100': 'opacity-0',
        isBackgroundVideo ? 'video-embed-object-fit-cover' : 'aspect-video max-h-[80vh]',
        centerVideo ? 'mx-auto' : '',
      ]"
    >
      <!-- EMBED -->
      <ClientOnly>
        <iframe
          v-if="embedUrl && play"
          :src="embedUrl"
          :title="videoTitle"
          frameborder="0"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
          referrerpolicy="strict-origin-when-cross-origin"
          class="size-full"
          @load="ready = true"
        />
      </ClientOnly>
    </div>

    <!-- COVER IMAGE -->
    <Transition v-if="coverImage" leave-active-class="transition-opacity duration-1000 ease-out delay-1000" leave-to-class="opacity-0">
      <div v-if="!ready" class="absolute inset-0">
        <AppImage
          :src="coverImage"
          :alt="useMapAltText(coverImage)"
          class="size-full object-cover"
          :class="{ 'brightness-[0.6]': !isBackgroundVideo }"
        />
      </div>
    </Transition>

    <!-- PLAY BUTTON -->
    <Transition name="fade-slow">
      <div v-if="!isBackgroundVideo && !ready" class="absolute inset-0">
        <Transition mode="out-in" name="fade-slow">
          <button
            v-if="!play && !ready"
            v-animate-zoom-in
            :aria-label="$t('aria_labels.play_video')"
            type="button"
            class="group absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            @click="play = true"
          >
            <IconPlay class="size-20 text-white transition-transform group-hover:scale-105 sm:size-26" />
          </button>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  videoUrl: {
    type: String,
    required: true,
  },
  videoTitle: {
    type: String,
    default: 'Video Player',
  },
  playerParams: {
    type: Object,
    default: () => ({
      controls: 0, // Disable controls
      autoplay: 1, // Enable autoplay
      muted: 0, // Mute the video (required for autoplay in most browsers)
      loop: 1, // Enable looping
      background: 1, // Hide Vimeo branding (requires Vimeo Pro or higher)
      dnt: 0, // Disable non-essential tracking cookies
    }),
  },
  isBackgroundVideo: {
    type: Boolean,
    default: false,
  },
  coverImage: {
    type: Object,
    default: () => ({}),
  },
  centerVideo: {
    type: Boolean,
    default: false,
  },
})

const play = ref(props.playerParams.autoplay === 1)

const ready = ref(false)

const embedUrl = computed(() => {
  const baseEmbedUrl = useGetVideoEmbedUrl(props.videoUrl)
  let params = { ...props.playerParams }
  params.autoplay = play.value ? 1 : 0 // Set autoplay based on play state
  params = new URLSearchParams(params)
  return `${baseEmbedUrl}?${params.toString()}`
})
</script>

<style>
/*
  Arbitrarily sized & positioned element that will
  contain the embedded video as background.
*/
.arbitrarily-placed-and-sized-parent-element {
  position: relative;
  top: 50px;
  left: 50px;

  width: 50vw;
  height: 20vh;
}

/*
  This element contains the iframe, will take it's size from
  it's parent and will behave like an image/video
  with object-fit: cover.
*/
.video-embed-object-fit-cover {
  position: absolute;

  /* ensure positioning regardless of parent flex settings */
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  width: 100%;
  height: 100%;

  /* important */
  container-type: size;

  /* cut off overflowing video */
  overflow: hidden;

  iframe {
    position: absolute;
    /* keep video centered both vertically and horizontally */
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: 100%;
    height: 100%;

    /* force aspect ratio based on parent container */
    @container (min-aspect-ratio: 16/9) {
      height: 56.25cqw;
    }

    @container (max-aspect-ratio: 16/9) {
      width: 177.78cqh;
    }

    /* prevent interaction */
    pointer-events: none;
  }
}
</style>
